// CreatePortfolioPage.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";
import {
    getMethod,
    postMethod,
    putMethod
} from "../../../../utils/api-service/api-service";
import { uploadToCloudinary, uploadToDigitalOcean } from "../../../../helpers/upload";

const CreatePortfolio = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [loading, setLoading] = useState(false);

    const [projectData, setProjectData] = useState({
        name: "",
        type: "HOSPITALITY",
        description: "",
        location: "",
        images: []
    });

    /* ============================= */
    /* FETCH PROJECT (EDIT MODE) */
    /* ============================= */
    useEffect(() => {
        if (isEditMode) fetchProject();
    }, [id]);

    const fetchProject = async () => {
        const response = await getMethod(`/portfolio/${id}`);
        if (response.success) {
            setProjectData({
                ...response.data,
                images: response.data.images?.map((img, i) => ({
                    id: `img-${i}`,
                    url: img.url,
                    isThumb: img.isThumb || false
                })) || []
            });
        } else {
            toast.error("Failed to fetch project");
        }
    };

    /* ============================= */
    /* IMAGE HANDLING */
    /* ============================= */

    const addImages = (files) => {
        const newImages = Array.from(files).map(file => ({
            id: `img-${crypto.randomUUID()}`,
            url: URL.createObjectURL(file),
            file,
            isThumb: false
        }));

        setProjectData(prev => ({
            ...prev,
            images: [...prev.images, ...newImages]
        }));
    };

    const removeImage = (index) => {
        const updated = projectData.images.filter((_, i) => i !== index);
        setProjectData(prev => ({ ...prev, images: updated }));
    };

    const setThumbnail = (index) => {
        const updated = projectData.images.map((img, i) => ({
            ...img,
            isThumb: i === index
        }));

        setProjectData(prev => ({ ...prev, images: updated }));
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        const oldIndex = projectData.images.findIndex(i => i.id === active.id);
        const newIndex = projectData.images.findIndex(i => i.id === over.id);

        const reordered = arrayMove(projectData.images, oldIndex, newIndex);

        setProjectData(prev => ({
            ...prev,
            images: reordered
        }));
    };

    /* ============================= */
    /* VALIDATION */
    /* ============================= */
    const validateForm = () => {
        if (projectData.type === "HOSPITALITY") {
            if (!projectData.name.trim()) return "Name is required";
            if (!projectData.description.trim()) return "Description required";
            if (!projectData.location.trim()) return "Location required";
        }

        if (projectData.images.length === 0)
            return "At least one image required";

        return null;
    };

    /* ============================= */
    /* SAVE */
    /* ============================= */
    const handleSave = async () => {
        const error = validateForm();
        if (error) {
            toast.error(error);
            return;
        }

        setLoading(true);

        try {
            const uploadPromises = projectData.images.map(async (img) => {
                // If already uploaded (edit mode)
                if (!img.file) return img;

                const res = await uploadToDigitalOcean(img.file, {
                    module: "portfolio",
                    slug: projectData.name,
                    type: projectData.type
                });

                return {
                    ...img,
                    url: res.secure_url,
                    public_id: res.public_id,
                    file: undefined
                };
            });

            // 🚀 PARALLEL UPLOAD
            const uploadedImages = await Promise.all(uploadPromises);

            const payload = {
                ...projectData,
                images: uploadedImages.map(img => ({
                    url: img.url,
                    public_id: img.public_id,
                    isThumb: img.isThumb
                }))
            };

            const response = isEditMode
                ? await putMethod(`/portfolio/${id}`, payload)
                : await postMethod(`/portfolio`, payload);

            if (response.success) {
                toast.success("Project saved successfully");
                navigate("/portfolio");
            } else {
                toast.error(response.message || "Save failed");
            }

        } catch (err) {
            toast.error("Error saving project");
        } finally {
            setLoading(false);
        }
    };


    /* ============================= */
    /* UI */
    /* ============================= */
    return (
        <div className="container py-4">
            <div className="card">
                <div className="card-body">

                    {/* BASIC DETAILS */}
                    <h5 className="border-bottom pb-2 mb-4">
                        Portfolio Project Details
                    </h5>

                    <div className="row">

                        {/* TYPE */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Project Type</label>
                            <select
                                className="form-select"
                                value={projectData.type}
                                onChange={(e) =>
                                    setProjectData(prev => ({
                                        ...prev,
                                        type: e.target.value
                                    }))
                                }
                            >
                                <option value="HOSPITALITY">Hospitality</option>
                                <option value="HOME">Home</option>
                            </select>
                        </div>

                        {/* CONDITIONAL FIELDS */}
                        {projectData.type === "HOSPITALITY" && (
                            <>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Name *</label>
                                    <input
                                        className="form-control"
                                        value={projectData.name}
                                        onChange={(e) =>
                                            setProjectData(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Location *</label>
                                    <input
                                        className="form-control"
                                        value={projectData.location}
                                        onChange={(e) =>
                                            setProjectData(prev => ({
                                                ...prev,
                                                location: e.target.value
                                            }))
                                        }
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Description *</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={projectData.description}
                                        onChange={(e) =>
                                            setProjectData(prev => ({
                                                ...prev,
                                                description: e.target.value
                                            }))
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* IMAGES SECTION */}
                    <h5 className="border-bottom pb-2 mt-4 mb-3">
                        Project Images *
                    </h5>

                    <input
                        type="file"
                        className="form-control mb-3"
                        multiple
                        accept="image/*"
                        onChange={(e) => addImages(e.target.files)}
                    />

                    <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={projectData.images.map(i => i.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {projectData.images.map((image, index) => (
                                <SortableImageCard
                                    key={image.id}
                                    image={image}
                                    index={index}
                                    onRemove={removeImage}
                                    onSetThumb={setThumbnail}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>

                    {/* ACTIONS */}
                    <div className="d-flex justify-content-end mt-4">
                        <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading ? "Saving..." : "Save Project"}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

/* ============================= */
/* SORTABLE IMAGE CARD */
/* ============================= */
const SortableImageCard = ({ image, index, onRemove, onSetThumb }) => {
    const { setNodeRef, attributes, listeners, transform, transition } =
        useSortable({ id: image.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div ref={setNodeRef} style={style} className="card mb-2 p-2 d-flex flex-row align-items-center">
            <div {...listeners} {...attributes} style={{ cursor: "grab" }}>
                <MdDragIndicator size={22} />
            </div>

            <img
                src={image.url}
                alt="project"
                style={{
                    width: "80px",
                    height: "80px",
                    objectFit: "cover",
                    marginLeft: "15px"
                }}
            />

            <div className="ms-3">
                <div className="form-check">
                    <input
                        type="radio"
                        name="thumbnail"
                        className="form-check-input"
                        checked={image.isThumb}
                        onChange={() => onSetThumb(index)}
                    />
                    <label className="form-check-label">
                        Set as Thumbnail
                    </label>
                </div>
            </div>

            <button
                className="btn btn-sm btn-danger ms-auto"
                onClick={() => onRemove(index)}
            >
                Remove
            </button>
        </div>
    );
};

export default CreatePortfolio;
